/**
 * Helper để lấy FCM device token
 * @returns {Promise<string|null>} FCM token hoặc null nếu không lấy được
 * 
 * Note: FCM token chỉ có thể lấy được khi:
 * - Browser hỗ trợ service worker
 * - User đã cấp quyền notification
 * - Đã cấu hình VAPID key
 * 
 * Nếu không thể lấy FCM token, vẫn trả về null (không fail)
 */
export async function getDeviceToken() {
  try {
    // Kiểm tra xem browser có hỗ trợ service worker không
    if (!('serviceWorker' in navigator)) {
      console.warn('[Device Token] Service Worker không được hỗ trợ');
      return null;
    }

    // Kiểm tra xem Firebase messaging có sẵn không
    try {
      const { getMessaging, getToken } = await import('firebase/messaging');
      const { FIREBASE_CONFIG } = await import('../config');
      const { initializeApp, getApps } = await import('firebase/app');
      
      // Lấy hoặc tạo app instance
      let app;
      const apps = getApps();
      if (apps.length === 0) {
        app = initializeApp(FIREBASE_CONFIG);
      } else {
        app = apps[0];
      }
      
      // Đăng ký service worker trước
      // Sử dụng scope rõ ràng và đảm bảo file được serve đúng
      let registration;
      try {
        registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        console.log('[Device Token] Service Worker registered:', registration.scope);
      } catch (swError) {
        console.error('[Device Token] ❌ Lỗi đăng ký service worker:', swError);
        console.error('[Device Token] Error message:', swError.message);
        console.error('[Device Token] Có thể file firebase-messaging-sw.js không được serve đúng cách');
        console.error('[Device Token] Kiểm tra: file có trong public/ folder?');
        throw swError; // Throw để catch block bên ngoài xử lý
      }
      
      const messaging = getMessaging(app, {
        serviceWorkerRegistration: registration
      });

      // Request permission
      console.log('[Device Token] Đang yêu cầu notification permission...');
      const permission = await Notification.requestPermission();
      console.log('[Device Token] Notification permission result:', permission);
      
      if (permission !== 'granted') {
        console.warn('[Device Token] ⚠️  Notification permission không được cấp:', permission);
        console.warn('[Device Token] User cần cho phép notification để lấy FCM token');
        return null;
      }
      
      console.log('[Device Token] ✅ Notification permission đã được cấp');

      // Lấy VAPID key từ config (nếu có)
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || FIREBASE_CONFIG.vapidKey;
      
      console.log('[Device Token] VAPID key check:', {
        fromEnv: !!process.env.REACT_APP_FIREBASE_VAPID_KEY,
        fromConfig: !!FIREBASE_CONFIG.vapidKey,
        hasVapidKey: !!vapidKey,
        vapidKeyLength: vapidKey?.length || 0,
        vapidKeyPreview: vapidKey ? vapidKey.substring(0, 20) + '...' : 'null'
      });
      
      if (!vapidKey) {
        console.error('[Device Token] ❌ VAPID key chưa được cấu hình. Device token sẽ không được lưu.');
        console.error('[Device Token] process.env.REACT_APP_FIREBASE_VAPID_KEY:', process.env.REACT_APP_FIREBASE_VAPID_KEY);
        console.error('[Device Token] FIREBASE_CONFIG.vapidKey:', FIREBASE_CONFIG.vapidKey);
        console.error('[Device Token] Hãy thêm REACT_APP_FIREBASE_VAPID_KEY vào file .env và restart frontend');
        return null;
      }
      
      console.log('[Device Token] ✅ VAPID key đã được cấu hình, đang lấy FCM token...');

      // Lấy token
      console.log('[Device Token] Đang gọi getToken() với VAPID key...');
      const token = await getToken(messaging, {
        vapidKey: vapidKey
      });

      if (token) {
        console.log('[Device Token] ✅ FCM token đã được lấy thành công!');
        console.log('[Device Token] Token preview:', token.substring(0, 30) + '...');
        console.log('[Device Token] Token length:', token.length);
        return token;
      } else {
        console.warn('[Device Token] ⚠️  getToken() trả về null/undefined');
        console.warn('[Device Token] Có thể do:');
        console.warn('  1. Service worker chưa sẵn sàng');
        console.warn('  2. VAPID key không hợp lệ');
        console.warn('  3. Firebase project chưa được cấu hình đúng');
        return null;
      }
    } catch (importError) {
      console.error('[Device Token] ❌ Firebase messaging error:', importError);
      console.error('[Device Token] Error message:', importError.message);
      console.error('[Device Token] Error stack:', importError.stack);
      return null;
    }
  } catch (error) {
    console.error('[Device Token] ❌ Error getting device token:', error);
    console.error('[Device Token] Error message:', error.message);
    console.error('[Device Token] Error stack:', error.stack);
    // Không throw error, chỉ return null
    return null;
  }
}

/**
 * Lấy thông tin device
 * @returns {Object} Device info
 */
export function getDeviceInfo() {
  const ua = navigator.userAgent;
  let platform = 'web';
  let deviceName = 'Unknown Device';

  // Detect platform
  if (/iPhone|iPad|iPod/.test(ua)) {
    platform = 'ios';
    deviceName = /iPhone/.test(ua) ? 'iPhone' : 'iPad';
  } else if (/Android/.test(ua)) {
    platform = 'android';
    deviceName = 'Android Device';
  } else if (/Windows/.test(ua)) {
    platform = 'web';
    deviceName = 'Windows';
  } else if (/Mac/.test(ua)) {
    platform = 'web';
    deviceName = 'Mac';
  } else if (/Linux/.test(ua)) {
    platform = 'web';
    deviceName = 'Linux';
  }

  // Get device ID (use localStorage để lưu device ID)
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('device_id', deviceId);
  }

  return {
    deviceId: deviceId,
    deviceName: deviceName,
    platform: platform,
    userAgent: ua
  };
}

/**
 * Setup message listener (optional - để nhận notifications khi app đang mở)
 */
/**
 * Setup message listener để nhận notifications khi app đang mở
 * @param {Function} callback - Callback function khi nhận được message
 */
export async function setupMessageListener(callback) {
  try {
    if (!('serviceWorker' in navigator)) {
      console.warn('[Device Token] Service Worker không được hỗ trợ');
      return;
    }

    const { getMessaging, onMessage } = await import('firebase/messaging');
    const { FIREBASE_CONFIG } = await import('../config');
    const { initializeApp, getApps } = await import('firebase/app');
    
    let app;
    const apps = getApps();
    if (apps.length === 0) {
      app = initializeApp(FIREBASE_CONFIG);
    } else {
      app = apps[0];
    }
    
    // Đăng ký service worker nếu chưa có
    const registration = await navigator.serviceWorker.ready;
    
    const messaging = getMessaging(app, {
      serviceWorkerRegistration: registration
    });
    
    onMessage(messaging, (payload) => {
      console.log('[Device Token] Message received (foreground):', payload);
      
      // Hiển thị notification nếu app đang mở
      if (Notification.permission === 'granted') {
        const notificationTitle = payload.notification?.title || payload.data?.title || 'Tosol Inverter';
        const notificationOptions = {
          body: payload.notification?.body || payload.data?.body || 'Bạn có thông báo mới',
          icon: payload.notification?.icon || payload.data?.icon || '/logo192.png',
          badge: '/logo192.png',
          tag: payload.data?.tag || 'default',
          data: payload.data || {}
        };
        
        if (payload.notification?.image || payload.data?.image) {
          notificationOptions.image = payload.notification?.image || payload.data?.image;
        }
        
        new Notification(notificationTitle, notificationOptions);
      }
      
      if (callback) {
        callback(payload);
      }
    });
    
    console.log('[Device Token] Message listener đã được setup');
  } catch (error) {
    console.warn('[Device Token] Error setting up message listener:', error.message);
  }
}

